import { GROUPED_SAFETY_CASE_STEP_LABELS } from './incidents/groupedSafetyCaseStepLabels';
import {
  followUpOwnerIsAssigned,
  patternOwnerIsAssigned,
  type SafetyReportReadinessState,
} from '../pages/acceptanceProof/safetyReportMvpPresentation';

export type SafetyWorkflowStepId =
  | 'detected'
  | 'review'
  | 'assign_action'
  | 'track_impact'
  | 'report'
  /** Pattern-owned presentation only — not Epic 93 CA/Impact/Report. */
  | 'management_decision'
  | 'managed_in_case';

export type SafetyWorkflowStepDefinition = {
  id: SafetyWorkflowStepId;
  label: string;
  compactLabel: string;
};

export const SAFETY_WORKFLOW_STEPS: SafetyWorkflowStepDefinition[] = [
  { id: 'detected', label: 'Observed', compactLabel: 'Observed' },
  { id: 'review', label: 'Evidence Review', compactLabel: 'Needs review' },
  { id: 'assign_action', label: 'Corrective Action', compactLabel: 'Action assigned' },
  { id: 'track_impact', label: 'Impact Measurement', compactLabel: 'Impact measured' },
  { id: 'report', label: 'Report preparation', compactLabel: 'Draft report' },
];

export const GROUPED_SAFETY_REPORT_WORKFLOW_STEPS: SafetyWorkflowStepDefinition[] = [
  {
    id: 'detected',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.riskPattern,
    compactLabel: GROUPED_SAFETY_CASE_STEP_LABELS.riskPattern,
  },
  {
    id: 'review',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.evidenceReview,
    compactLabel: GROUPED_SAFETY_CASE_STEP_LABELS.evidenceReview,
  },
  {
    id: 'assign_action',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.correctiveAction,
    compactLabel: GROUPED_SAFETY_CASE_STEP_LABELS.correctiveAction,
  },
  {
    id: 'track_impact',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.impactTracking,
    compactLabel: GROUPED_SAFETY_CASE_STEP_LABELS.impactTracking,
  },
  {
    id: 'report',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.safetyReport,
    compactLabel: GROUPED_SAFETY_CASE_STEP_LABELS.safetyReport,
  },
];

export type SafetyWorkflowProgressInput = {
  hasSelectedSafetyEvent?: boolean;
  /** Grouped recurring-risk guided workflow: Detected / Understand risk uses this instead of event selection. */
  groupedGuidedRecurringRisk?: boolean;
  riskContextReviewed?: boolean;
  reviewComplete?: boolean;
  assignActionComplete?: boolean;
  patternOwnerLabel?: string | null;
  followUpOwnerLabel?: string | null;
  correctiveLinked?: boolean;
  followUpMeasured?: boolean;
  reportReady?: boolean;
  readinessState?: SafetyReportReadinessState | null;
};

export type SafetyWorkflowProgress = {
  currentStepId: SafetyWorkflowStepId;
  completedStepIds: SafetyWorkflowStepId[];
  nextActionLabel: string;
  nextActionStepId: SafetyWorkflowStepId;
};

function isStepComplete(stepId: SafetyWorkflowStepId, input: SafetyWorkflowProgressInput): boolean {
  switch (stepId) {
    case 'detected':
      if (input.groupedGuidedRecurringRisk) {
        // System detection only (recurring-risk row selected). Human “understand risk” is not local checklist truth.
        return Boolean(input.hasSelectedSafetyEvent);
      }
      return Boolean(input.hasSelectedSafetyEvent);
    case 'review':
      return Boolean(input.reviewComplete);
    case 'assign_action':
      if (typeof input.assignActionComplete === 'boolean') {
        return input.assignActionComplete;
      }
      return (
        followUpOwnerIsAssigned(input.followUpOwnerLabel)
        || (
          patternOwnerIsAssigned(input.patternOwnerLabel)
          && Boolean(input.reviewComplete)
        )
      );
    case 'track_impact':
      return Boolean(input.followUpMeasured);
    case 'report':
      return Boolean(input.reportReady || input.readinessState === 'ready');
    case 'management_decision':
    case 'managed_in_case':
      return false;
    default:
      return false;
  }
}

function defaultNextAction(stepId: SafetyWorkflowStepId): string {
  switch (stepId) {
    case 'detected':
      return 'Review required';
    case 'review':
      return 'Confirm whether this recurring risk pattern requires corrective action';
    case 'assign_action':
      return 'Assign action owner';
    case 'track_impact':
      return 'Record impact measurement after action is taken';
    case 'report':
      return 'Prepare report';
    case 'management_decision':
      return 'Decide whether this pattern requires a Safety Case';
    case 'managed_in_case':
      return 'Continue management in the Safety Case';
    default:
      return 'Continue workflow';
  }
}

function groupedSafetyReportNextAction(stepId: SafetyWorkflowStepId): string {
  switch (stepId) {
    case 'detected':
      return 'Review the recurring risk';
    case 'review':
      return 'Review evidence and confirm whether this recurring risk is a true safety issue.';
    case 'assign_action':
      return 'Assign an action owner and define the corrective action';
    case 'track_impact':
      return 'Record impact tracking after corrective action is taken';
    case 'report':
      return 'Prepare the Safety Report draft';
    case 'management_decision':
      return 'Decide whether this pattern requires a Safety Case';
    case 'managed_in_case':
      return 'Continue management in the Safety Case';
    default:
      return 'Continue workflow';
  }
}

export function resolveSafetyWorkflowProgress(
  input: SafetyWorkflowProgressInput,
): SafetyWorkflowProgress {
  const completedStepIds = SAFETY_WORKFLOW_STEPS
    .filter((step) => isStepComplete(step.id, input))
    .map((step) => step.id);

  let currentStepId: SafetyWorkflowStepId = 'detected';
  for (const step of SAFETY_WORKFLOW_STEPS) {
    if (!isStepComplete(step.id, input)) {
      currentStepId = step.id;
      break;
    }
    currentStepId = step.id;
  }

  const allComplete = SAFETY_WORKFLOW_STEPS.every((step) => isStepComplete(step.id, input));
  const nextActionStepId = allComplete
    ? 'report'
    : SAFETY_WORKFLOW_STEPS.find((step) => !isStepComplete(step.id, input))?.id || currentStepId;

  return {
    currentStepId,
    completedStepIds,
    nextActionLabel: defaultNextAction(nextActionStepId),
    nextActionStepId,
  };
}

export function safetyWorkflowStepStatus(
  stepId: SafetyWorkflowStepId,
  progress: SafetyWorkflowProgress,
  options?: { blockedStepIds?: SafetyWorkflowStepId[]; lockedStepIds?: SafetyWorkflowStepId[] },
): 'complete' | 'current' | 'upcoming' | 'locked' | 'blocked' {
  if ((options?.blockedStepIds || []).includes(stepId)) return 'blocked';
  if ((options?.lockedStepIds || []).includes(stepId)) return 'locked';
  if (progress.completedStepIds.includes(stepId)) return 'complete';
  if (progress.currentStepId === stepId) return 'current';
  return 'upcoming';
}

export function buildSafetyReportWorkflowProgress(args: {
  hasSelectedSafetyEvent: boolean;
  projectionReviewed: boolean;
  outcomeRecorded: boolean;
  patternOwnerLabel: string;
  followUpOwnerLabel: string;
  projectionCurrentActionLabel: string;
  proofPageState: string;
  safetyReportReadinessState: SafetyReportReadinessState;
}): SafetyWorkflowProgress {
  return resolveSafetyWorkflowProgress({
    hasSelectedSafetyEvent: args.hasSelectedSafetyEvent,
    reviewComplete: args.projectionReviewed,
    patternOwnerLabel: args.patternOwnerLabel,
    followUpOwnerLabel: args.followUpOwnerLabel,
    correctiveLinked: Boolean(
      args.projectionCurrentActionLabel
      && !/pending|not linked|not assigned|create follow-up/i.test(String(args.projectionCurrentActionLabel || '')),
    ),
    followUpMeasured: args.outcomeRecorded,
    reportReady: args.proofPageState === 'READY' || args.safetyReportReadinessState === 'ready',
    readinessState: args.safetyReportReadinessState,
  });
}

export function buildPatternReviewWorkflowProgress(args: {
  patternOwnerLabel?: string | null;
  followUpOwnerLabel?: string | null;
  reviewComplete?: boolean;
  followUpMeasured?: boolean;
  reportReady?: boolean;
}): SafetyWorkflowProgress {
  return resolveSafetyWorkflowProgress({
    hasSelectedSafetyEvent: true,
    reviewComplete: Boolean(args.reviewComplete),
    patternOwnerLabel: args.patternOwnerLabel,
    followUpOwnerLabel: args.followUpOwnerLabel,
    followUpMeasured: Boolean(args.followUpMeasured),
    reportReady: Boolean(args.reportReady),
  });
}

/** Macro workflow for grouped recurring-risk guided review (Epic 93). */
export function buildGroupedGuidedWorkflowProgress(args: {
  patternOwnerLabel?: string | null;
  followUpOwnerLabel?: string | null;
  /** Recurring-risk row is open for review (system detected). */
  hasSelectedRecurringRisk?: boolean;
  representativeReviewComplete?: boolean;
  followUpMeasured?: boolean;
  reportReady?: boolean;
  /**
   * Review-flow milestone: the action plan is in motion and the reviewer can move
   * on to impact tracking without claiming the corrective action is operationally done.
   */
  assignActionReadyToAdvance?: boolean;
  /** Corrective action is complete only after follow-up status is Closed. */
  correctiveActionComplete?: boolean;
}): SafetyWorkflowProgress {
  const progress = resolveSafetyWorkflowProgress({
    groupedGuidedRecurringRisk: true,
    hasSelectedSafetyEvent: Boolean(args.hasSelectedRecurringRisk ?? true),
    reviewComplete: Boolean(args.representativeReviewComplete),
    assignActionComplete: Boolean(
      args.assignActionReadyToAdvance ?? args.correctiveActionComplete,
    ),
    patternOwnerLabel: args.patternOwnerLabel,
    followUpOwnerLabel: args.followUpOwnerLabel,
    followUpMeasured: Boolean(args.followUpMeasured),
    reportReady: Boolean(args.reportReady),
  });

  return {
    ...progress,
    nextActionLabel: groupedSafetyReportNextAction(progress.nextActionStepId),
  };
}
